export const ICEBOX_PRIMARY_DISPOSITION = {
  NoContactMade: 'no_contact_made',
  BadPhoneNumber: 'bad_phone_number',
  DnqCredit: 'dnq_credit',
  DnqDownPayment: 'dnq_down_payment',
  DnqIncome: 'dnq_income',
  DnqLtv: 'dnq_ltv',
  DoNotCall: 'do_not_call',
  DuplicateLead: 'duplicate_lead',
  PurchaseDecidedNotToProceed: 'purchase_decided_not_to_proceed',
  PurchaseNoHouse: 'purchase_no_house',
  RefinanceNoNetTangibleBenefit: 'refinance_no_net_tangible_benefit',
  WentWithAnotherLender: 'went_with_another_lender',
  Funded: 'funded',
  ReferredForHeloc: 'referred_for_heloc',
  ProductNotAvailable: 'product_not_available',
} as const

export type IceboxPrimaryDisposition =
  (typeof ICEBOX_PRIMARY_DISPOSITION)[keyof typeof ICEBOX_PRIMARY_DISPOSITION]

export const ICEBOX_PRODUCT_REQUESTED = {
  VaIrrrl: 'va_irrrl',
  VaCashOut: 'va_cash_out',
  FhaStreamline: 'fha_streamline',
  Dscr: 'dscr',
  Arm: 'arm',
  BankStatementLoan: 'bank_statement_loan',
  ManufacturedHome: 'manufactured_home',
  ConstructionLoan: 'construction_loan',
} as const

export type IceboxProductRequested =
  (typeof ICEBOX_PRODUCT_REQUESTED)[keyof typeof ICEBOX_PRODUCT_REQUESTED]

export interface IceboxDispositionSubsection {
  value: string
  label: string
}

export interface IceboxProductDispositionOption {
  value: IceboxProductRequested
  label: string
  description: string
  note?: string
  subsections: readonly IceboxDispositionSubsection[]
}

export interface IceboxDispositionOption {
  value: IceboxPrimaryDisposition
  label: string
  description: string
  note?: string
  subsections: readonly IceboxDispositionSubsection[]
  products?: readonly IceboxProductDispositionOption[]
}

export const ICEBOX_NO_CONTACT_MIN_ATTEMPTS_SUBSECTION = 'attempts_7_plus'
export const ICEBOX_REFERRED_TO_HELOC_TEAM_SUBSECTION = 'referred_to_heloc_team'

export const ICEBOX_DISPOSITION_OPTIONS = [
  {
    value: ICEBOX_PRIMARY_DISPOSITION.NoContactMade,
    label: 'No Contact Made',
    description: 'Lead was never reached despite outreach attempts.',
    note: 'No lead should reach Icebox for No Contact until a minimum of 7 attempts across phone, text, and email have been completed per the Speed to Lead protocol.',
    subsections: [
      { value: 'attempts_1_to_2', label: '1 to 2 attempts' },
      { value: 'attempts_3_to_4', label: '3 to 4 attempts' },
      { value: 'attempts_5_to_6', label: '5 to 6 attempts' },
      {
        value: ICEBOX_NO_CONTACT_MIN_ATTEMPTS_SUBSECTION,
        label: '7+ attempts (full contact protocol exhausted)',
      },
    ],
  },
  {
    value: ICEBOX_PRIMARY_DISPOSITION.BadPhoneNumber,
    label: 'Bad Phone Number',
    description: 'Phone number on file is disconnected, invalid, or incorrect.',
    subsections: [
      { value: 'number_disconnected', label: 'Number disconnected' },
      { value: 'wrong_party', label: 'Number belongs to wrong party' },
      {
        value: 'no_ring_no_voicemail',
        label: 'Number never rings / no voicemail',
      },
      { value: 'email_only', label: 'Email only / no valid phone obtained' },
    ],
  },
  {
    value: ICEBOX_PRIMARY_DISPOSITION.DnqCredit,
    label: 'DNQ - Credit',
    description: 'Lead does not qualify based on credit profile.',
    note: 'Document the credit score range even when estimated. Flags future recapture timing.',
    subsections: [
      { value: 'below_500', label: 'Below 500' },
      { value: '500_to_539', label: '500 to 539' },
      { value: '540_to_579', label: '540 to 579' },
      { value: '580_to_619', label: '580 to 619' },
      { value: '620_to_639', label: '620 to 639' },
      {
        value: 'score_unknown',
        label: 'Did not authorize credit pull / score unknown',
      },
    ],
  },
  {
    value: ICEBOX_PRIMARY_DISPOSITION.DnqDownPayment,
    label: 'DNQ - Down Payment',
    description:
      'Lead does not have sufficient verified funds for down payment and/or closing costs.',
    subsections: [
      { value: 'no_down_payment', label: 'No down payment available' },
      {
        value: 'insufficient_funds',
        label: 'Insufficient funds for loan type minimum',
      },
      {
        value: 'funds_not_documentable',
        label: 'Funds not sourced / not documentable',
      },
      {
        value: 'gift_funds_ineligible',
        label: 'Gift funds only / not eligible',
      },
    ],
  },
  {
    value: ICEBOX_PRIMARY_DISPOSITION.DnqIncome,
    label: 'DNQ - Income',
    description: 'Lead does not meet income qualification requirements.',
    subsections: [
      {
        value: 'income_too_low',
        label: 'Income too low for target purchase price or loan amount',
      },
      {
        value: 'income_not_documentable',
        label:
          'Income not documentable (cash only, undisclosed self employment)',
      },
      {
        value: 'declining_income',
        label: 'Declining income / negative trend',
      },
      {
        value: 'employment_gap',
        label: 'Employment gap or too new to job',
      },
      {
        value: 'self_employed_less_than_2_years',
        label: 'Self employed / less than 2 years history',
      },
      { value: 'dti_too_high', label: 'DTI exceeds guideline maximum' },
    ],
  },
  {
    value: ICEBOX_PRIMARY_DISPOSITION.DnqLtv,
    label: 'DNQ - LTV',
    description: 'Loan to value is outside acceptable guideline parameters.',
    subsections: [
      {
        value: 'insufficient_equity_refinance',
        label: 'Insufficient equity for refinance',
      },
      {
        value: 'property_value_shortfall',
        label: 'Property value does not support loan amount',
      },
      { value: 'appraisal_shortfall', label: 'Appraisal shortfall' },
      {
        value: 'second_mortgage_subordination',
        label: 'Second mortgage / HELOC subordination issue',
      },
    ],
  },
  {
    value: ICEBOX_PRIMARY_DISPOSITION.DoNotCall,
    label: 'Do Not Call',
    description: 'Lead has formally requested no further contact.',
    note: 'This disposition triggers immediate removal from all outreach queues. No re-engagement without legal/compliance review.',
    subsections: [
      {
        value: 'verbally_requested_no_contact',
        label: 'Verbally requested no contact',
      },
      { value: 'written_dnc_request', label: 'Submitted written DNC request' },
      { value: 'registered_dnc_list', label: 'Registered on DNC list' },
      {
        value: 'hostile_interaction',
        label: 'Hostile / threatening interaction documented',
      },
    ],
  },
  {
    value: ICEBOX_PRIMARY_DISPOSITION.DuplicateLead,
    label: 'Duplicate Lead',
    description: 'Lead already exists in the pipeline under another record.',
    subsections: [
      {
        value: 'assigned_to_multiple_los',
        label: 'Same lead assigned to multiple LOs',
      },
      {
        value: 'reentered_different_source',
        label: 'Lead re-entered from different source',
      },
      {
        value: 'merged_into_active_record',
        label: 'Merged into existing active record',
      },
      {
        value: 'funded_client_reentered',
        label: 'Previously funded client re-entered as new lead',
      },
    ],
  },
  {
    value: ICEBOX_PRIMARY_DISPOSITION.PurchaseDecidedNotToProceed,
    label: 'Purchase - Decided Not To Proceed',
    description:
      'Qualified purchase client has chosen to pause or cancel homebuying plans.',
    subsections: [
      {
        value: 'market_affordability',
        label: 'Market conditions / affordability concerns',
      },
      {
        value: 'life_circumstances',
        label: 'Personal / life circumstances changed',
      },
      {
        value: 'decided_to_rent',
        label: 'Decided to rent / not purchase at this time',
      },
      {
        value: 'cold_feet',
        label: 'Cold feet / not emotionally ready',
      },
      {
        value: 'rates_payment_not_acceptable',
        label: 'Rates / payment not acceptable',
      },
    ],
  },
  {
    value: ICEBOX_PRIMARY_DISPOSITION.PurchaseNoHouse,
    label: 'Purchase - No House',
    description: 'Client is qualified and ready but unable to find a property.',
    note: 'This is a warm icebox. These clients should remain in a nurture sequence and be re-engaged every 30 to 45 days.',
    subsections: [
      {
        value: 'searching_no_offer',
        label: 'Actively searching / no offer accepted',
      },
      {
        value: 'lost_multiple_offers',
        label: 'Lost multiple offers / competition',
      },
      { value: 'paused_home_search', label: 'Paused home search' },
      {
        value: 'agent_relationship_dissolved',
        label: 'Agent relationship dissolved',
      },
      {
        value: 'market_inventory_frustration',
        label: 'Market / inventory frustration',
      },
    ],
  },
  {
    value: ICEBOX_PRIMARY_DISPOSITION.RefinanceNoNetTangibleBenefit,
    label: 'Refinance - No Net Tangible Benefit',
    description:
      'Refinance does not produce a measurable financial benefit for the client under current conditions.',
    note: 'Document the rate comparison and monthly savings calculation. These leads are rate-trigger candidates and should be re-engaged when market conditions shift.',
    subsections: [
      {
        value: 'rate_higher_or_equal',
        label: 'Rate is higher or equal to current rate',
      },
      {
        value: 'break_even_too_long',
        label: 'Break even period exceeds client timeline',
      },
      {
        value: 'savings_insufficient',
        label: 'Monthly savings insufficient (under $50 to $75 threshold)',
      },
      {
        value: 'plans_to_sell',
        label: 'Client plans to sell within break even window',
      },
      {
        value: 'blended_rate_not_supported',
        label: 'Blended rate analysis does not support transaction',
      },
    ],
  },
  {
    value: ICEBOX_PRIMARY_DISPOSITION.WentWithAnotherLender,
    label: 'Went With Another Lender',
    description: 'Client has chosen to proceed with a competing lender.',
    note: 'If timing allows, a final save attempt should be made before this disposition is applied.',
    subsections: [
      {
        value: 'rate_pricing',
        label: 'Rate / pricing cited as reason',
      },
      {
        value: 'existing_relationship',
        label: 'Pre-existing relationship with another lender',
      },
      {
        value: 'realtor_referred_lender',
        label: 'Referred by Realtor to specific lender',
      },
      { value: 'did_not_share_reason', label: 'Did not share reason' },
      {
        value: 'already_in_process_elsewhere',
        label: 'Was already in process elsewhere when contacted',
      },
    ],
  },
  {
    value: ICEBOX_PRIMARY_DISPOSITION.Funded,
    label: 'Funded',
    description: 'Loan successfully closed and funded.',
    note: 'Funded clients are strong referral sources. Add to post-close nurture sequence and schedule a re-engagement check-in at 6 and 12 months.',
    subsections: [
      { value: 'purchase', label: 'Purchase loan funded' },
      { value: 'rate_term_refinance', label: 'Rate/term refinance funded' },
      { value: 'cash_out_refinance', label: 'Cash-out refinance funded' },
      { value: 'va_loan', label: 'VA loan funded' },
      { value: 'fha_loan', label: 'FHA loan funded' },
      { value: 'jumbo', label: 'Jumbo / non-conforming loan funded' },
    ],
  },
  {
    value: ICEBOX_PRIMARY_DISPOSITION.ReferredForHeloc,
    label: 'Referred for HELOC',
    description:
      'Client was referred to the HELOC team for a home equity line of credit.',
    note: 'Confirm the referral was handed off to the HELOC team. The current first-lien lead is iceboxed; the HELOC team owns the active follow-up.',
    subsections: [
      {
        value: 'wants_heloc_keep_first_lien',
        label: 'Wants HELOC / keeping existing first mortgage',
      },
      {
        value: 'cash_out_better_as_heloc',
        label: 'Cash-out need better served by a HELOC',
      },
      {
        value: 'debt_consolidation_heloc',
        label: 'HELOC for debt consolidation',
      },
      {
        value: 'home_improvement_heloc',
        label: 'HELOC for home improvement / renovation',
      },
      {
        value: ICEBOX_REFERRED_TO_HELOC_TEAM_SUBSECTION,
        label: 'Referred to HELOC team / handed off',
      },
    ],
  },
  {
    value: ICEBOX_PRIMARY_DISPOSITION.ProductNotAvailable,
    label: 'Product Not Available',
    description:
      'Client requires a loan product that is not currently offered.',
    subsections: [],
    products: [
      {
        value: ICEBOX_PRODUCT_REQUESTED.VaIrrrl,
        label: 'VA IRRRL',
        description: 'VA streamline refinance product. Not currently offered.',
        note: 'VA IRRRL clients should be tagged for re-engagement if product becomes available.',
        subsections: [
          {
            value: 'existing_va_loan',
            label:
              'Client has existing VA loan and is requesting VA streamline refinance',
          },
          {
            value: 'referred_for_va_irrrl',
            label: 'Client was referred specifically for VA IRRRL product',
          },
          {
            value: 'va_rate_term_no_cash_out',
            label: 'Rate and term refinance on VA loan / no cash out',
          },
        ],
      },
      {
        value: ICEBOX_PRODUCT_REQUESTED.VaCashOut,
        label: 'VA Cash Out',
        description: 'VA cash-out refinance product. Not currently offered.',
        note: 'VA Cash Out clients should be tagged for re-engagement if product becomes available.',
        subsections: [
          {
            value: 'va_cash_out_full',
            label: 'Client requesting full VA cash-out refinance',
          },
          {
            value: 'va_cash_out_debt_consolidation',
            label: 'Client using cash-out for debt consolidation',
          },
          {
            value: 'va_cash_out_home_improvement',
            label: 'Client using cash-out for home improvements',
          },
        ],
      },
      {
        value: ICEBOX_PRODUCT_REQUESTED.FhaStreamline,
        label: 'FHA Streamline',
        description: 'FHA streamline refinance product. Not currently offered.',
        note: 'FHA Streamline clients with existing FHA loans may be eligible for a conventional refinance depending on equity and credit.',
        subsections: [
          {
            value: 'existing_fha_loan',
            label:
              'Client has existing FHA loan and is requesting FHA streamline refinance',
          },
          {
            value: 'reduced_documentation_fha',
            label: 'Client seeking reduced documentation FHA refi',
          },
          {
            value: 'no_appraisal_limited_income',
            label: 'No appraisal / limited income documentation request',
          },
        ],
      },
      {
        value: ICEBOX_PRODUCT_REQUESTED.Dscr,
        label: 'DSCR',
        description:
          'Investor cash flow loan product based on property income, not personal income.',
        note: 'Document property address, anticipated rental income, and loan amount requested.',
        subsections: [
          {
            value: 'investor_rental_income_only',
            label: 'Client is an investor qualifying on rental income only',
          },
          {
            value: 'no_personal_income_review',
            label:
              'Client requesting no income documentation / no personal income review',
          },
          {
            value: 'short_term_rental_cash_flow',
            label: 'Short term rental / Airbnb cash flow qualification request',
          },
          {
            value: 'portfolio_dscr',
            label:
              'Client has multiple investment properties seeking portfolio DSCR',
          },
        ],
      },
      {
        value: ICEBOX_PRODUCT_REQUESTED.Arm,
        label: 'ARM',
        description:
          'Adjustable rate mortgage products including 5/1, 7/1, and 10/1 ARM structures.',
        note: 'Clients requesting ARMs for qualification purposes may be workable on a fixed rate product with adjusted loan amount or purchase price.',
        subsections: [
          { value: 'arm_5_1', label: 'Client requesting 5/1 ARM' },
          { value: 'arm_7_1', label: 'Client requesting 7/1 ARM' },
          { value: 'arm_10_1', label: 'Client requesting 10/1 ARM' },
          {
            value: 'arm_for_qualification',
            label:
              'Client requesting ARM for qualification / lower initial payment',
          },
          {
            value: 'short_term_hold_strategy',
            label:
              'Client citing short term hold / ARM strategy for investment property',
          },
        ],
      },
      {
        value: ICEBOX_PRODUCT_REQUESTED.BankStatementLoan,
        label: 'Bank Statement Loan',
        description:
          'Alternative income documentation product using 12 to 24 months of bank statements in lieu of tax returns.',
        note: 'Document gross deposits, business type, and loan amount for future re-engagement or referral strategy.',
        subsections: [
          {
            value: 'self_employed_write_downs',
            label:
              'Self employed client with significant write-downs / low taxable income',
          },
          {
            value: 'bank_statement_12_month',
            label: 'Client requesting 12-month bank statement qualification',
          },
          {
            value: 'bank_statement_24_month',
            label: 'Client requesting 24-month bank statement qualification',
          },
          {
            value: 'contractor_non_qm',
            label: '1099 contractor requesting non-QM income documentation',
          },
          {
            value: 'business_owner_strong_deposits',
            label:
              'Business owner with strong deposits but insufficient tax return income',
          },
        ],
      },
      {
        value: ICEBOX_PRODUCT_REQUESTED.ManufacturedHome,
        label: 'Manufactured Home',
        description:
          'Financing for manufactured or mobile homes. Not currently offered.',
        note: 'Document home type (single/double-wide, on owned vs. leased land) and loan amount for future re-engagement.',
        subsections: [
          {
            value: 'manufactured_home_purchase',
            label: 'Client purchasing a manufactured home',
          },
          {
            value: 'manufactured_home_refinance',
            label: 'Client refinancing an existing manufactured home loan',
          },
          {
            value: 'manufactured_home_leased_land',
            label: 'Manufactured home on leased / non-owned land',
          },
        ],
      },
      {
        value: ICEBOX_PRODUCT_REQUESTED.ConstructionLoan,
        label: 'Construction Loan',
        description:
          'Ground-up construction, construction-to-permanent, and renovation financing. Not currently offered.',
        note: 'Document project type (ground-up vs. reno), estimated build cost/timeline, lot ownership, and loan amount for future re-engagement or referral.',
        subsections: [
          {
            value: 'ground_up_construction',
            label: 'Client requesting ground-up / new construction financing',
          },
          {
            value: 'construction_to_permanent',
            label:
              'Client requesting construction-to-permanent (single-close) loan',
          },
          {
            value: 'renovation_construction',
            label: 'Client requesting renovation / rehab construction loan',
          },
          {
            value: 'owns_lot_needs_build',
            label: 'Client owns the lot and needs financing to build',
          },
        ],
      },
    ],
  },
] as const satisfies readonly IceboxDispositionOption[]

export const ICEBOX_PRIMARY_DISPOSITION_VALUES = ICEBOX_DISPOSITION_OPTIONS.map(
  (option) => option.value
)

export const ICEBOX_PRODUCT_REQUESTED_VALUES = Object.values(
  ICEBOX_PRODUCT_REQUESTED
)

export function findIceboxPrimaryDispositionOption(
  primaryDisposition?: string | null
): IceboxDispositionOption | undefined {
  return ICEBOX_DISPOSITION_OPTIONS.find(
    (option) => option.value === primaryDisposition
  )
}

export function findIceboxProductDispositionOption(
  productRequested?: string | null
): IceboxProductDispositionOption | undefined {
  return ICEBOX_DISPOSITION_OPTIONS.flatMap(
    (option) => option.products || []
  ).find((option) => option.value === productRequested)
}

export function getIceboxSubsectionOptions({
  primaryDisposition,
  productRequested,
}: {
  primaryDisposition?: string | null
  productRequested?: string | null
}): readonly IceboxDispositionSubsection[] {
  if (primaryDisposition === ICEBOX_PRIMARY_DISPOSITION.ProductNotAvailable) {
    return (
      findIceboxProductDispositionOption(productRequested)?.subsections || []
    )
  }

  return (
    findIceboxPrimaryDispositionOption(primaryDisposition)?.subsections || []
  )
}

export function isValidIceboxDisposition({
  primaryDisposition,
  subsectionDisposition,
  productRequested,
}: {
  primaryDisposition?: string | null
  subsectionDisposition?: string | null
  productRequested?: string | null
}): boolean {
  if (!primaryDisposition || !subsectionDisposition) return false

  const primaryOption = findIceboxPrimaryDispositionOption(primaryDisposition)
  if (!primaryOption) return false

  if (primaryDisposition === ICEBOX_PRIMARY_DISPOSITION.ProductNotAvailable) {
    if (!productRequested) return false
    const productOption = findIceboxProductDispositionOption(productRequested)
    return Boolean(
      productOption?.subsections.some(
        (subsection) => subsection.value === subsectionDisposition
      )
    )
  }

  if (productRequested) return false

  return primaryOption.subsections.some(
    (subsection) => subsection.value === subsectionDisposition
  )
}

export function getIceboxDispositionDisplayLabel({
  primaryDisposition,
  subsectionDisposition,
  productRequested,
  notes,
}: {
  primaryDisposition?: string | null
  subsectionDisposition?: string | null
  productRequested?: string | null
  notes?: string | null
}): string | null {
  const primaryOption = findIceboxPrimaryDispositionOption(primaryDisposition)
  if (!primaryOption || !subsectionDisposition) return notes || null

  const productOption =
    primaryDisposition === ICEBOX_PRIMARY_DISPOSITION.ProductNotAvailable
      ? findIceboxProductDispositionOption(productRequested)
      : undefined
  const subsectionOption = getIceboxSubsectionOptions({
    primaryDisposition,
    productRequested,
  }).find((subsection) => subsection.value === subsectionDisposition)

  if (!subsectionOption) return notes || primaryOption.label
  if (productOption) {
    return `${primaryOption.label} - ${productOption.label}: ${subsectionOption.label}`
  }

  return `${primaryOption.label}: ${subsectionOption.label}`
}
